import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableHead,
  TableContainer,
  TableHeadCell,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  Paginator,
} from '@safedns/skydns-ui-react-lib';

import {
  useGetUserQuery,
  useGetCollectionsQuery,
  useUpdateCollectionPriorityMutation,
} from '../../services/ADapi';

import usePagination from '../../hooks/usePagination';
import CollectionsListItem from './CollectionsListItem';
import FetchError from '../UI/fetch-error/FetchError';

import './Collections.scss';

import { actionTableCellStyle } from '../../styles/tableStyles';

const initialDragAndDropState = {
  draggedFrom: null,
  draggedTo: null,
  isDragging: false,
  originalOrder: [],
  updatedOrder: [],
};

const filterCollections = (collections, searchQuery) => {
  const lowerCaseSearchQuery = searchQuery.toLowerCase();

  return collections.filter((user) => {
    return user.name.toLowerCase().includes(lowerCaseSearchQuery);
  });
};

const CollectionsList = ({ searchQuery }) => {
  const {
    data: { user_id },
  } = useGetUserQuery();

  const {
    data: collections,
    isError,
    isLoading,
  } = useGetCollectionsQuery(user_id, {
    skip: !user_id,
  });
  const [dragAndDrop, setDragAndDrop] = useState(initialDragAndDropState);
  const [collectionsList, setCollectionsList] = useState([]);
  const priority = useRef();

  const [updateCollectionPriority] = useUpdateCollectionPriorityMutation();

  useEffect(() => {
    if (collections) {
      const sortedCollections = [...collections].sort(
        (a, b) => a.priority - b.priority
      );

      setCollectionsList(sortedCollections);
    }
  }, [collections, isLoading]);

  useEffect(() => {
    priority.current = collectionsList.map((item, index) => {
      return {
        collection_id: item.id,
        priority: index + 1,
      };
    });
  }, [collectionsList]);

  const changePriorityHandler = async () => {
    try {
      await updateCollectionPriority({
        user_id,
        payload: { data: priority.current },
      }).unwrap();
    } catch (error) {
      console.error(error);
    }
  };

  const onDragStart = (e) => {
    const initialPosition = Number(e.currentTarget.dataset.position);

    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: initialPosition,
      isDragging: true,
      originalOrder: collectionsList,
    });

    e.dataTransfer.setData('text/html', '');
  };

  const onDragOver = (e) => {
    e.preventDefault();

    let newList = dragAndDrop.originalOrder;

    const draggedFrom = dragAndDrop.draggedFrom;
    const draggedTo = Number(e.currentTarget.dataset.position);
    const itemDragged = newList[draggedFrom];
    const remainingItems = newList.filter(
      (_item, index) => index !== draggedFrom
    );

    newList = [
      ...remainingItems.slice(0, draggedTo),
      itemDragged,
      ...remainingItems.slice(draggedTo),
    ];

    if (draggedTo !== dragAndDrop.draggedTo) {
      setDragAndDrop({
        ...dragAndDrop,
        updatedOrder: newList,
        draggedTo: draggedTo,
      });
    }
  };

  const onDrop = () => {
    setCollectionsList(dragAndDrop.updatedOrder);

    setDragAndDrop({
      ...dragAndDrop,
      draggedFrom: null,
      draggedTo: null,
      isDragging: false,
    });
  };

  const onDragLeave = () => {
    setDragAndDrop({
      ...dragAndDrop,
      draggedTo: null,
    });
  };

  const itemsCount = 5;
  const { page, gaps, totalPages, nextPage, prevPage, setPage } = usePagination(
    {
      contentPerPage: itemsCount,
      count: collectionsList && collectionsList.length,
    }
  );

  let result = collectionsList;
  if (searchQuery.length > 0) {
    result = filterCollections(collectionsList, searchQuery);
  }

  if (isError) {
    return <FetchError />;
  }

  if (collectionsList.length === 0) {
    return <p className="collections-list-empty">Коллекции не найдены</p>;
  }

  return (
    <React.Fragment>
      <TableContainer sx={{ paddingTop: 16 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell></TableHeadCell>
              <TableHeadCell sx={{ textAlign: 'left' }}>
                Приоритет
              </TableHeadCell>
              <TableHeadCell sx={{ textAlign: 'left' }}>
                Коллекции
              </TableHeadCell>
              <TableHeadCell sx={{ textAlign: 'left' }}>Профиль</TableHeadCell>
              <TableHeadCell sx={{ textAlign: 'left' }}>
                Пользователи
              </TableHeadCell>
              <TableHeadCell sx={actionTableCellStyle}></TableHeadCell>
              <TableHeadCell sx={actionTableCellStyle}></TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {result.length === 0 ? (
              <TableRow>
                <TableCell colSpan="6">
                  <Typography variant="subtitle2">
                    Нет коллекций, соответствующих заданному фильтру.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              result.map((listItem, index) => (
                <CollectionsListItem
                  key={listItem.id}
                  id={listItem.id}
                  priority={listItem.priority}
                  name={listItem.name}
                  policy={listItem.profile}
                  users={listItem.ad_users.length}
                  dataposition={index}
                  onDragStart={onDragStart}
                  onDragOver={onDragOver}
                  onDrop={onDrop}
                  onDragLeave={onDragLeave}
                  onChangePriority={changePriorityHandler}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {result?.length > itemsCount && (
        <Paginator
          nextPage={nextPage}
          prevPage={prevPage}
          page={page}
          gaps={gaps}
          setPage={setPage}
          totalPages={totalPages}
        />
      )}
    </React.Fragment>
  );
};

export default CollectionsList;
